import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Image as ImageIcon, Loader2, RotateCw, Search, X } from "lucide-react";
import type { TemplateStudioType } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ReferencePagination } from "@/components/template-studio-library/reference-pagination";
import { ReferenceImage } from "@/components/template-studio-library/reference-image";
import { queryClient } from "@/lib/queryClient";

type SavedAd = {
  id: string;
  customName?: string | null;
  defaultNameSnapshot?: string | null;
  adUnitNameSnapshot?: string | null;
  durableUrl?: string | null;
  imageUrlOriginal?: string | null;
  thumbnailUrl?: string | null;
  personaName?: string | null;
  productName?: string | null;
  createdAt?: string | null;
};
type SavedAdsPage = { items: SavedAd[]; total: number };
type LibraryMembership = { savedAdIds?: string[] };
type Status = { state: "pending" | "success" | "error"; message?: string };
type Props = {
  brandId: string; enabled: boolean; available: TemplateStudioType[];
  typesLoading?: boolean; typesError?: boolean; refetchTypes?: () => void;
};

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "include", ...options });
  const body = response.status === 204 ? null : await response.json().catch(() => ({}));
  if (!response.ok) {
    const payload = body as { message?: string; error?: string } | null;
    throw new Error(payload?.message || payload?.error || `Request failed (${response.status})`);
  }
  return body as T;
}

// Keep three rows per server page, matching the grid's Tailwind breakpoints.
export function myAdsPageSize(viewportWidth: number) {
  const columns = viewportWidth >= 1280 ? 4 : viewportWidth >= 1024 ? 3 : viewportWidth >= 640 ? 2 : 1;
  return columns * 3;
}

export function MyAdsPanel({ brandId, enabled, available, typesLoading, typesError, refetchTypes }: Props) {
  const [pageSize, setPageSize] = useState(() => myAdsPageSize(typeof window === "undefined" ? 1280 : window.innerWidth));
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [personaId, setPersonaId] = useState("all");
  const [productId, setProductId] = useState("all");
  const [narrativeAngleId, setNarrativeAngleId] = useState("all");
  const [adUnitTypeId, setAdUnitTypeId] = useState("all");
  const [projectId, setProjectId] = useState("all");
  const [sort, setSort] = useState<"newest" | "oldest" | "name">("newest");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Map<string, SavedAd>>(new Map());
  const [typeByAd, setTypeByAd] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const [summary, setSummary] = useState("");
  const lockRef = useRef(false);
  const mountedRef = useRef(true);
  const contextRef = useRef(brandId);
  const base = `/api/brands/${encodeURIComponent(brandId)}/template-studio/library-images`;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    const resize = () => {
      const nextSize = myAdsPageSize(window.innerWidth);
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(0);
      }
    };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [pageSize]);
  useEffect(() => {
    setSearch(""); setDebouncedSearch("");
    setPersonaId("all"); setProductId("all"); setNarrativeAngleId("all");
    setAdUnitTypeId("all"); setProjectId("all"); setSort("newest"); setPage(0);
    setSelected(new Map()); setTypeByAd({}); setStatuses({}); setSummary("");
    contextRef.current = brandId;
  }, [brandId]);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => { setPage(0); }, [debouncedSearch, personaId, productId, narrativeAngleId, adUnitTypeId, projectId, sort]);
  useEffect(() => {
    setSelected(new Map()); setTypeByAd({}); setStatuses({}); setSummary("");
  }, [debouncedSearch, personaId, productId, narrativeAngleId, adUnitTypeId, projectId, sort]);

  const referenceOptions = { enabled: !!brandId && enabled, retry: false, staleTime: 60_000 } as const;
  const personasUrl = `/api/brands/${encodeURIComponent(brandId)}/personas`;
  const productsUrl = `/api/brands/${encodeURIComponent(brandId)}/products`;
  const anglesUrl = `/api/brands/${encodeURIComponent(brandId)}/narrative-angles`;
  const adUnitsUrl = `/api/brands/${encodeURIComponent(brandId)}/ad-units`;
  const projectsUrl = `/api/brands/${encodeURIComponent(brandId)}/projects`;
  const personas = useQuery<Array<{ id: string; name: string }>>({ queryKey: [personasUrl], queryFn: () => request(personasUrl), ...referenceOptions });
  const products = useQuery<Array<{ id: string; name: string }>>({ queryKey: [productsUrl], queryFn: () => request(productsUrl), ...referenceOptions });
  const angles = useQuery<Array<{ id: string; name: string }>>({ queryKey: [anglesUrl], queryFn: () => request(anglesUrl), ...referenceOptions });
  const adUnits = useQuery<Array<{ id: string; name: string; formatType?: string | null }>>({ queryKey: [adUnitsUrl], queryFn: () => request(adUnitsUrl), ...referenceOptions });
  const projects = useQuery<Array<{ id: string; name: string }>>({ queryKey: [projectsUrl], queryFn: () => request(projectsUrl), ...referenceOptions });
  const listUrl = useMemo(() => {
    const params = new URLSearchParams({
      status: "saved", scope: "brand", staticReferenceOnly: "1", paged: "1",
      limit: String(pageSize), offset: String(page * pageSize), sort,
    });
    if (personaId !== "all") params.set("personaId", personaId);
    if (productId !== "all") params.set("productId", productId);
    if (narrativeAngleId !== "all") params.set("narrativeAngleId", narrativeAngleId);
    if (adUnitTypeId !== "all") params.set("adUnitTypeId", adUnitTypeId);
    if (projectId !== "all") params.set("projectId", projectId);
    if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
    return `/api/brands/${encodeURIComponent(brandId)}/generated-ads?${params}`;
  }, [brandId, debouncedSearch, personaId, productId, narrativeAngleId, adUnitTypeId, projectId, page, sort, pageSize]);
  const adsQuery = useQuery<SavedAdsPage>({
    queryKey: [listUrl], queryFn: () => request<SavedAdsPage>(listUrl),
    enabled: !!brandId && enabled, retry: 2, staleTime: 30_000,
  });
  const membershipQuery = useQuery<LibraryMembership[]>({
    queryKey: [base, "membership"], queryFn: () => request<LibraryMembership[]>(base),
    enabled: !!brandId && enabled, retry: false,
  });
  const importedIds = useMemo(() => new Set((membershipQuery.data ?? []).flatMap((image) => image.savedAdIds ?? [])), [membershipQuery.data]);
  const ads = adsQuery.data?.items ?? [];
  const total = adsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const anyFilter = !!search.trim() || [personaId, productId, narrativeAngleId, adUnitTypeId, projectId].some((v) => v !== "all");
  const selectedEntries = [...selected.values()];
  const failures = selectedEntries.filter((ad) => statuses[ad.id]?.state === "error");
  const importTargets = failures.length ? failures : selectedEntries;
  const pending = Object.values(statuses).some((status) => status.state === "pending");
  const filterSelect = (label: string, value: string, setter: (value: string) => void, options?: Array<{ id: string; name: string }>) => (
    <label className="min-w-0 flex-1 space-y-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
      <select aria-label={label} value={value} disabled={pending} onChange={(event) => { setPage(0); setter(event.target.value); }}
        className="h-9 w-full rounded-md border border-violet-200 bg-white px-2 text-xs font-medium normal-case tracking-normal text-slate-700">
        <option value="all">All {label.toLowerCase()}</option>
        {label === "Projects" && <option value="none">(no project)</option>}
        {(options ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
      </select>
    </label>
  );
  const toggle = (ad: SavedAd) => {
    setSelected((previous) => {
      const next = new Map(previous);
      if (next.has(ad.id)) next.delete(ad.id); else next.set(ad.id, ad);
      return next;
    });
    setSummary("");
  };
  const importSelected = async () => {
    const targets = failures.length ? failures : selectedEntries;
    if (lockRef.current || typesLoading || typesError || !targets.length ||
      targets.some((ad) => !available.some((type) => type.id === typeByAd[ad.id]))) return;
    lockRef.current = true;
    const selectedTypes = { ...typeByAd };
    const capturedBrand = brandId;
    setSummary("");
    let added = 0;
    let failed = 0;
    const succeeded: string[] = [];
    try {
      for (const ad of targets) {
        if (!mountedRef.current || contextRef.current !== capturedBrand) break;
        setStatuses((previous) => ({ ...previous, [ad.id]: { state: "pending" } }));
        try {
          await request(`${base}/from-saved-ad`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ savedAdId: ad.id, typeIds: [selectedTypes[ad.id]] }),
          });
          added++;
          succeeded.push(ad.id);
          if (!mountedRef.current || contextRef.current !== capturedBrand) break;
          setStatuses((previous) => ({ ...previous, [ad.id]: { state: "success" } }));
        } catch (error) {
          if (!mountedRef.current || contextRef.current !== capturedBrand) break;
          failed++;
          setStatuses((previous) => ({ ...previous, [ad.id]: { state: "error", message: (error as Error).message } }));
        }
      }
      if (mountedRef.current && contextRef.current === capturedBrand) {
        setSelected((previous) => {
          const next = new Map(previous);
          succeeded.forEach((id) => next.delete(id));
          return next;
        });
        setSummary(`${added} imported${failed ? ` · ${failed} failed` : ""}.`);
      }
    } finally {
      // A committed old-brand import still invalidates that brand's cache;
      // it must never update the newly mounted brand's selection or feedback.
      if (added) await queryClient.invalidateQueries({ queryKey: [base] });
      lockRef.current = false;
    }
  };

  const clearFilters = () => {
    setSearch(""); setPersonaId("all"); setProductId("all"); setNarrativeAngleId("all");
    setAdUnitTypeId("all"); setProjectId("all"); setSort("newest"); setPage(0);
    setSelected(new Map()); setTypeByAd({}); setStatuses({}); setSummary("");
  };

  return <section className="space-y-4" aria-label="My Ads references">
    {typesLoading && <p role="status" className="text-sm text-slate-600">Loading available board types…</p>}
    {typesError && <div role="alert" className="rounded-lg border border-rose-200 bg-white p-3 text-sm text-rose-700">
      Unable to load board types. Importing is unavailable until they load.
      <Button type="button" variant="outline" size="sm" className="ml-2" onClick={refetchTypes}>Retry board types</Button>
    </div>}
    <div className="rounded-xl border border-violet-100 bg-white p-3 sm:p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input aria-label="Search my ads" value={search} disabled={pending} onChange={(event) => { setPage(0); setSearch(event.target.value); }}
              placeholder="Search saved ads…" className="h-10 w-full rounded-md border border-violet-200 bg-[#fcfbff] pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-violet-400" />
          </label>
          <label className="space-y-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Sort
            <select aria-label="Sort ads" value={sort} disabled={pending} onChange={(event) => { setPage(0); setSort(event.target.value as typeof sort); }}
              className="h-10 min-w-36 rounded-md border border-violet-200 bg-white px-3 text-xs font-medium normal-case tracking-normal text-slate-700">
              <option value="newest">Newest</option><option value="oldest">Oldest</option><option value="name">Name</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {filterSelect("Personas", personaId, setPersonaId, personas.data)}
          {filterSelect("Products", productId, setProductId, products.data)}
          {filterSelect("Angles", narrativeAngleId, setNarrativeAngleId, angles.data)}
          {filterSelect("Ad types", adUnitTypeId, setAdUnitTypeId, adUnits.data?.filter((unit) => !unit.formatType || unit.formatType === "static"))}
          {filterSelect("Projects", projectId, setProjectId, projects.data)}
        </div>
        {anyFilter && <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={clearFilters} className="w-fit text-xs text-violet-800"><X className="mr-1 h-3.5 w-3.5" />Clear search and filters</Button>}
      </div>
    </div>

    {selectedEntries.length > 0 && <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><p className="text-sm font-semibold text-violet-950">{selectedEntries.length} selected</p>
          <p className="text-xs text-slate-600">Selection stays with you as you browse pages.</p></div>
        <Button type="button" variant="ghost" size="sm" onClick={() => { setSelected(new Map()); setTypeByAd({}); setStatuses({}); setSummary(""); }} disabled={pending}>Clear selection</Button>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2">
        {selectedEntries.map((ad) => <li key={ad.id} className="flex max-w-full items-center gap-2 rounded-lg border border-violet-200 bg-white px-2.5 py-2">
          <span className="max-w-40 truncate text-xs font-medium text-slate-700">{ad.customName || ad.defaultNameSnapshot || ad.adUnitNameSnapshot || "Saved ad"}</span>
          <select aria-label={`Board type for ${ad.customName || ad.defaultNameSnapshot || ad.id}`} value={typeByAd[ad.id] || ""}
            disabled={pending || available.length === 0} onChange={(event) => setTypeByAd((previous) => ({ ...previous, [ad.id]: event.target.value }))}
            className="h-8 max-w-36 rounded-md border border-violet-200 bg-white px-2 text-xs">
            <option value="">Choose type…</option>{available.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
          </select>
          {statuses[ad.id]?.state === "pending" ? <Loader2 className="h-4 w-4 animate-spin text-violet-700" aria-label="Importing" />
            : statuses[ad.id]?.state === "error" ? <span className="max-w-36 truncate text-[11px] text-rose-700" title={statuses[ad.id].message}>Failed: {statuses[ad.id].message}</span>
              : statuses[ad.id]?.state === "success" ? <Check className="h-4 w-4 text-emerald-700" aria-label="Imported" /> : null}
          <button type="button" aria-label={`Remove ${ad.customName || ad.defaultNameSnapshot || "selected ad"}`} disabled={pending}
            onClick={() => { setSelected((previous) => { const next = new Map(previous); next.delete(ad.id); return next; }); setTypeByAd((previous) => { const next = { ...previous }; delete next[ad.id]; return next; }); }}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>
        </li>)}
      </ul>
      {!available.length && <p className="mt-3 text-xs text-amber-800">Enable a board type before importing ads.</p>}
      <Button type="button" onClick={importSelected} disabled={pending || !available.length || importTargets.some((ad) => !typeByAd[ad.id])}
        className="mt-3 bg-violet-700 text-white hover:bg-violet-800">
        {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
        {failures.length ? "Retry failed imports" : "Add selected to library"}
      </Button>
    </div>}
    {summary && <p role="status" className="rounded-lg border border-violet-100 bg-white px-3 py-2 text-xs font-medium text-slate-700">{failures.length ? <RotateCw className="mr-1 inline h-3.5 w-3.5" /> : null}{summary}</p>}

    {adsQuery.isError ? <div role="alert" className="rounded-xl border border-rose-200 bg-white p-5">
      <p className="text-sm font-semibold text-slate-800">Couldn’t load saved ads.</p><p className="mt-1 text-xs text-slate-600">Your selection is still here. Try again.</p>
      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => adsQuery.refetch()}>Retry</Button>
    </div> : adsQuery.isLoading ? <div role="status" aria-label="Loading saved ads" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => <div key={item} className="space-y-3 rounded-xl border border-violet-100 bg-white p-2.5"><div className="aspect-square animate-pulse rounded-lg bg-violet-50" /><div className="h-4 w-2/3 animate-pulse rounded bg-violet-50" /><div className="h-8 rounded bg-violet-50" /></div>)}
    </div> : ads.length === 0 ? <div className="rounded-xl border border-violet-100 bg-white px-6 py-12 text-center">
      <ImageIcon className="mx-auto h-8 w-8 text-violet-300" /><p className="mt-3 text-sm font-semibold text-slate-800">{anyFilter ? "No ads match these filters" : "No saved ads yet"}</p>
      <p className="mt-1 text-xs text-slate-500">{anyFilter ? "Try widening your search." : "Saved static ads for this brand will appear here."}</p>
      {anyFilter && <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={clearFilters} className="mt-2 text-violet-800">Clear filters</Button>}
    </div> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {ads.map((ad) => {
        const name = ad.customName || ad.defaultNameSnapshot || ad.adUnitNameSnapshot || "Saved ad";
        const image = ad.thumbnailUrl || ad.durableUrl || ad.imageUrlOriginal || "";
        const checked = selected.has(ad.id);
        const alreadyImported = importedIds.has(ad.id);
        const status = statuses[ad.id];
        return <Card key={ad.id} className={`overflow-hidden rounded-xl bg-white shadow-[0_5px_18px_-16px_rgba(50,34,90,.45)] ${checked ? "border-violet-500 ring-1 ring-violet-300" : "border-violet-100"}`} data-testid={`my-ad-card-${ad.id}`}>
          <div className="relative">
            {image ? <ReferenceImage sources={[...new Set([image, ad.durableUrl || "", ad.imageUrlOriginal || ""].filter(Boolean))]} alt={`Preview of ${name}`} testId={`my-ad-preview-${ad.id}`} />
              : <div className="flex aspect-square items-center justify-center bg-violet-50 text-center text-xs text-slate-500">Preview unavailable</div>}
            {!image && <span className="absolute bottom-2 left-2 rounded bg-white/90 px-2 py-1 text-[10px] text-slate-600">Preview unavailable</span>}
          </div>
          <CardContent className="space-y-2.5 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><h3 className="truncate text-sm font-semibold text-slate-800" title={name}>{name}</h3>
                <p className="truncate text-[11px] text-slate-500">{ad.adUnitNameSnapshot || "Static ad"}{ad.personaName ? ` · ${ad.personaName}` : ""}{ad.productName ? ` · ${ad.productName}` : ""}</p></div>
              {alreadyImported && <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-800">In library</span>}
            </div>
            {status?.state === "error" && <p role="alert" className="line-clamp-2 text-xs text-rose-700">{status.message}</p>}
            <Button type="button" size="sm" variant={checked ? "default" : "outline"} aria-pressed={checked}
              disabled={pending} onClick={() => toggle(ad)} className={`w-full ${checked ? "bg-violet-700 text-white hover:bg-violet-800" : "border-violet-200 text-violet-800"}`}>
              {checked ? <Check className="mr-2 h-4 w-4" /> : <ImageIcon className="mr-2 h-4 w-4" />}
              {checked ? "Selected" : "Select ad"}
            </Button>
          </CardContent>
        </Card>;
      })}
    </div>}
    {!adsQuery.isError && !adsQuery.isLoading && total > 0 && <ReferencePagination page={safePage + 1} pageCount={pageCount} total={total} onPageChange={(next) => setPage(next - 1)} source="My Ads" />}
    {membershipQuery.isError && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      Library membership status is unavailable. <Button variant="link" size="sm" className="h-auto p-0 text-amber-900" onClick={() => membershipQuery.refetch()}>Retry</Button>
    </div>}
  </section>;
}
